import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dices, KeyRound, Swords, UserPlus } from 'lucide-react';

import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';
import { useInvite } from '@/hooks/useInvite';
import { useRankedQueue } from '@/hooks/useRankedQueue';
import { useStartMatch } from '@/hooks/useStartMatch';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import { AcceptInviteSheet } from '@/components/backgammon/AcceptInviteSheet';
import { InviteSheet } from '@/components/backgammon/InviteSheet';
import { useResumableMatch } from '@/hooks/useResumableMatch';
import { RankedQueueSheet } from '@/components/backgammon/RankedQueueSheet';
import type { Player } from '@qlashgeminiz/qgammon-rules';
import { useGetLeaderboardQuery, useGetMeQuery } from '@/services/qgammonApi';

interface BackgammonLobbyProps {
  /** Hide the leaderboard peek when the Hub's Classifica section shows it. */
  showLeaderboard?: boolean;
}

/**
 * QGammon surface entry (Fase 6 part 1): header, a "live board coming soon"
 * note, and the live ELO leaderboard from qgammon-api. The interactive board +
 * Socket.IO live play land in part 2 (porting the @qgammon board, which needs
 * the rules/types vendored into the TMA).
 */
export function BackgammonLobby({ showLeaderboard = true }: BackgammonLobbyProps) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const startMatch = useStartMatch();
  const { data, isLoading, isError } = useGetLeaderboardQuery(undefined, {
    skip: !showLeaderboard,
  });
  const { data: meData } = useGetMeQuery();
  const players = data?.players ?? [];
  const [starting, setStarting] = useState(false);
  /** Why "Play vs bot" didn't start (e.g. the concurrent-match cap). */
  const [startError, setStartError] = useState<string | null>(null);
  const [matchLength, setMatchLength] = useState<1 | 3 | 5>(3);
  const ranked = useRankedQueue();
  const invite = useInvite();
  // Una partita che esiste e di cui il giocatore non sa niente: nasce quando
  // qualcuno accetta un suo invito mentre lui non e' in ascolto. Col deep link
  // e' il caso NORMALE, non il raro. V. `useResumableMatch`.
  const resumable = useResumableMatch();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);

  // On pairing, navigate to the match exactly like the practice flow does —
  // same route, same `state.perspective` (the server's `color`). The brief
  // "found" frame in the sheet shows for a beat first.
  useEffect(() => {
    if (ranked.status !== 'paired' || !ranked.paired) return;
    haptics.notification('success');
    const { matchId, color } = ranked.paired;
    const timer = window.setTimeout(() => {
      startMatch({ game: 'qgammon', matchId, perspective: color });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [ranked.status, ranked.paired, startMatch, haptics]);

  // Creator side: when our invite is accepted, the gameserver pushes
  // `invite:accepted` (carrying OUR colour). Navigate to the match with that
  // perspective, exactly like ranked pairing does.
  useEffect(() => {
    if (invite.status !== 'accepted' || !invite.accepted) return;
    haptics.notification('success');
    const { matchId, color } = invite.accepted;
    const timer = window.setTimeout(() => {
      setInviteOpen(false);
      startMatch({ game: 'qgammon', matchId, perspective: color });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [invite.status, invite.accepted, startMatch, haptics]);

  function joinRanked() {
    haptics.impact('medium');
    ranked.join();
  }

  function openInvite() {
    haptics.impact('light');
    setInviteOpen(true);
  }

  function openAccept() {
    haptics.impact('light');
    setAcceptOpen(true);
  }

  // Acceptor side: navigate with OUR colour returned by POST accept.
  function onInviteAccepted(result: { matchId: string; color: Player }) {
    haptics.notification('success');
    setAcceptOpen(false);
    startMatch({ game: 'qgammon', matchId: result.matchId, perspective: result.color });
  }

  async function playVsBot() {
    setStarting(true);
    setStartError(null);
    haptics.impact('medium');
    try {
      // Logto Bearer if signed in, else dev-bypass headers (dev only).
      const headers = await qgammonAuthHeaders(true);
      const res = await fetch(`${env.qgammonWsUrl}/matches/practice`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ botUsername: 'qbot', matchLength }),
      });
      // A practice match needs no counterparty, so it's the cheapest way to
      // mint live sessions — the server caps them per user and answers 409
      // `too_many_active_matches` (with the cap in `max`). Say so instead of
      // showing the generic "couldn't start".
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          max?: number;
        };
        haptics.notification('error');
        setStartError(
          body.error === 'too_many_active_matches'
            ? body.max != null
              ? t('home.atMatchCapN', { max: body.max })
              : t('home.atMatchCap')
            : t('home.startError'),
        );
        setStarting(false);
        return;
      }
      if (!res.ok) throw new Error(`practice create failed: ${res.status}`);
      const match = (await res.json()) as { matchId: string; humanColor: 'W' | 'B' };
      startMatch({ game: 'qgammon', matchId: match.matchId, perspective: match.humanColor, vsBot: true });
    } catch {
      haptics.notification('error');
      setStartError(t('home.startError'));
      setStarting(false);
    }
  }

  return (
    <div className="space-y-4 px-4 py-4">
      <header className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand/15 text-brand">
          <Dices aria-hidden className="h-6 w-6" strokeWidth={2.5} />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-fg">{t('home.title')}</h1>
          <p className="text-sm text-fg-muted">{t('home.subtitle')}</p>
        </div>
      </header>

      {meData && (
        <div className="space-y-0.5">
          <p className="text-xs text-fg-muted">
            {t('identity.playingAs', {
              name: meData.user.displayName || meData.user.username,
              username: meData.user.username,
            })}
          </p>
          {meData.user.username === 'lg_devlocal' && (
            <p className="text-xs font-medium text-warning">
              {t('identity.sharedDev')}
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={joinRanked}
        disabled={ranked.status !== 'idle'}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
      >
        <Swords aria-hidden className="h-4 w-4" />
        {t('ranked.ranked')}
      </button>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-fg-muted">{t('home.matchLength')}</p>
        <div className="flex gap-2">
          {([1, 3, 5] as const).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setMatchLength(n)}
              className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition-colors ${
                matchLength === n
                  ? 'border-brand bg-brand/15 text-brand'
                  : 'border-border bg-bg-secondary text-fg-muted'
              }`}
            >
              {t(`home.pt${n}`)}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={playVsBot}
        disabled={starting}
        className="w-full rounded-xl bg-brand px-4 py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-60"
      >
        {starting ? t('home.starting') : t('home.playVsBot')}
      </button>

      {startError && <p className="text-sm text-danger">{startError}</p>}

      {resumable && (
        <button
          type="button"
          onClick={() => {
            haptics.impact('medium');
            startMatch({
              game: 'qgammon',
              matchId: resumable.matchId,
              // ⚠️ La prospettiva viaggia SEMPRE: senza, `BackgammonMatch`
              // ripiega su 'W' e meta' delle volte mostra il tabellone dal lato
              // sbagliato — pedine proprie in alto, punteggi invertiti.
              perspective: resumable.perspective,
            });
          }}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-brand/40 bg-brand/10 px-3 py-2.5 text-left"
        >
          <span className="text-sm font-semibold text-fg">
            {t('resume.title', { name: resumable.opponentName })}
          </span>
          <span className="text-xs font-medium text-brand">{t('resume.cta')}</span>
        </button>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={openInvite}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          <UserPlus aria-hidden className="h-4 w-4 text-brand" />
          {t('invite.challenge')}
        </button>
        <button
          type="button"
          onClick={openAccept}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          <KeyRound aria-hidden className="h-4 w-4 text-brand" />
          {t('invite.haveCode')}
        </button>
      </div>

      {showLeaderboard && (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-fg">{t('home.leaderboard')}</h2>

          {isError ? (
            <p className="text-sm text-danger">{t('home.loadError')}</p>
          ) : isLoading ? (
            <p className="text-sm text-fg-muted">…</p>
          ) : players.length === 0 ? (
            <p className="text-sm text-fg-muted">{t('home.empty')}</p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-bg-secondary">
              {players.map((p, i) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-6 text-sm tabular-nums text-fg-muted">{i + 1}</span>
                  <span className="flex-1 truncate text-sm text-fg">
                    {p.displayName || p.username}
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-brand">{p.elo}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {(ranked.status !== 'idle' || ranked.notice) && <RankedQueueSheet queue={ranked} />}

      {inviteOpen && (
        <InviteSheet
          invite={invite}
          onClose={() => {
            // Cancellation of a pending invite is handled inside the sheet.
            setInviteOpen(false);
          }}
        />
      )}

      {acceptOpen && (
        <AcceptInviteSheet onAccepted={onInviteAccepted} onClose={() => setAcceptOpen(false)} />
      )}
    </div>
  );
}
